/**
 * Overview Tab Component
 *
 * Displays strategy summary including:
 * - P&L summary cards
 * - Volume breakdown
 * - Funding fee status
 * - Real-time price information
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Grid, Paper } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import type { StrategyInfo, Order, FuturesOrder } from '../../../types/strategy';
import { buildPnlSourceView } from '../../../utils/pnlSourceQuality';
import { traderTheme as theme } from '../traderDashboardTheme';

interface OverviewTabProps {
  strategy: StrategyInfo;
}

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  color?: string;
  icon: React.ReactNode;
}

function SummaryCard({ title, value, subtitle, color, icon }: SummaryCardProps) {
  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: theme.bg.secondary,
        border: `1px solid ${theme.border}`,
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: theme.text.secondary, fontSize: '0.75rem', display: 'block', mb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: color || theme.text.primary,
              fontWeight: 600,
              fontFamily: 'monospace',
              fontSize: '1.25rem',
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ color: color || theme.text.secondary, fontSize: '0.7rem' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: color || theme.text.muted, opacity: 0.7 }}>{icon}</Box>
      </Box>
    </Paper>
  );
}

function formatCurrency(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '-';
  const sign = value >= 0 ? '+' : '';
  return `${sign}$${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function formatPercent(value: number | null | undefined): string {
  if (value == null) return '-';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatVolume(value: number | null | undefined, decimals = 6): string {
  if (value == null) return '-';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals > 4 ? 4 : decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Calculate average price from fills (volume-weighted average)
 */
function calculateAvgPriceFromFills(fills: { price: number; volume: number }[]): number {
  if (!fills || fills.length === 0) return 0;

  let totalVolume = 0;
  let totalValue = 0;

  for (const fill of fills) {
    totalVolume += fill.volume;
    totalValue += fill.price * fill.volume;
  }

  return totalVolume > 0 ? totalValue / totalVolume : 0;
}

/**
 * Get order value (total notional) handling market orders without fills
 */
function getOrderValue(order: Order): number {
  // Use total if available
  if (order.total && order.total > 0) {
    return order.total;
  }
  // Try fills
  if (order.fills && order.fills.length > 0) {
    const avgPrice = calculateAvgPriceFromFills(order.fills);
    return avgPrice * order.executed_volume;
  }
  // Try executed_price
  if (order.executed_price && order.executed_price > 0) {
    return order.executed_price * order.executed_volume;
  }
  // Fallback to order price (works for limit orders)
  return order.price * order.executed_volume;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ strategy }) => {
  const { t } = useTranslation();
  const ctx = strategy.execution_context;

  // Calculate spot costs and revenue
  const spotSummary = useMemo(() => {
    const orders = Object.values(ctx.orders) as Order[];

    const buyOrders = orders.filter((o) => o.side === 'Bid' && o.executed_volume > 0);
    const sellOrders = orders.filter((o) => o.side === 'Ask' && o.executed_volume > 0);

    const totalCost = buyOrders.reduce((sum, o) => {
      const cost = getOrderValue(o);
      const rate = o.quote_to_usdt_rate || 1.0;
      return sum + cost * rate;
    }, 0);

    const totalRevenue = sellOrders.reduce((sum, o) => {
      const revenue = getOrderValue(o);
      const rate = o.quote_to_usdt_rate || 1.0;
      return sum + revenue * rate;
    }, 0);

    const totalFees = orders.reduce((sum, o) => {
      const fee = o.paid_fee || 0;
      const rate = o.quote_to_usdt_rate || 1.0;
      return sum + fee * rate;
    }, 0);

    return { totalCost, totalRevenue, totalFees, buyCount: buyOrders.length, sellCount: sellOrders.length };
  }, [ctx.orders]);

  // Calculate futures P&L
  const futuresSummary = useMemo(() => {
    const orders = Object.values(ctx.futures_orders) as FuturesOrder[];

    // In one-way mode (position_side=Both), we only have short positions
    // Open short: Ask (sell) orders
    // Close short: ALL Bid (buy) orders - not just reduce_only ones!
    const openShortOrders = orders.filter((o) => o.side === 'Ask');
    const closeShortOrders = orders.filter((o) => o.side === 'Bid');

    const totalShortValue = openShortOrders.reduce((sum, o) => {
      const price = o.avg_price || o.price;
      return sum + price * o.executed_volume;
    }, 0);

    const totalCloseValue = closeShortOrders.reduce((sum, o) => {
      const price = o.avg_price || o.price;
      return sum + price * o.executed_volume;
    }, 0);

    // Calculate volumes for matching
    const openedVolume = openShortOrders.reduce((sum, o) => sum + o.executed_volume, 0);
    const closedVolume = closeShortOrders.reduce((sum, o) => sum + o.executed_volume, 0);

    // Realized P&L from closing shorts = short_value - close_value (profit if we closed lower)
    const realizedPnl = totalShortValue - totalCloseValue;

    const totalCommission = orders.reduce((sum, o) => sum + (o.commission || 0), 0);

    return {
      totalShortValue,
      totalCloseValue,
      realizedPnl,
      totalCommission,
      openCount: openShortOrders.length,
      closeCount: closeShortOrders.length,
      openedVolume,
      closedVolume,
    };
  }, [ctx.futures_orders]);

  // Volume matching analysis
  const volumeMatching = useMemo(() => {
    const orders = Object.values(ctx.orders) as Order[];

    // Spot: purchased vs sold
    const spotPurchased = orders
      .filter((o) => o.side === 'Bid' && o.executed_volume > 0)
      .reduce((sum, o) => sum + o.executed_volume, 0);
    const spotSold = orders
      .filter((o) => o.side === 'Ask' && o.executed_volume > 0)
      .reduce((sum, o) => sum + o.executed_volume, 0);
    const spotDiff = spotPurchased - spotSold;
    const spotMatched = Math.abs(spotDiff) < 0.00000001; // Consider matched if diff < 1 satoshi

    // Futures: opened short vs closed short
    const futuresOpened = futuresSummary.openedVolume;
    const futuresClosed = futuresSummary.closedVolume;
    const futuresDiff = futuresOpened - futuresClosed;
    const futuresMatched = Math.abs(futuresDiff) < 0.00000001;

    // Overall matching status
    const allMatched = spotMatched && futuresMatched;

    return {
      spot: {
        purchased: spotPurchased,
        sold: spotSold,
        diff: spotDiff,
        matched: spotMatched,
      },
      futures: {
        opened: futuresOpened,
        closed: futuresClosed,
        diff: futuresDiff,
        matched: futuresMatched,
      },
      allMatched,
    };
  }, [ctx.orders, futuresSummary]);

  // Funding fee summary
  const fundingSummary = useMemo(() => {
    const fundingFee = ctx.funding_fee;
    const pnlFetched = ctx.pnl_snapshot?.fetched;

    // Consider synchronized if:
    // 1. funding_fee.synchronized is explicitly true, OR
    // 2. We have pnl_snapshot.fetched data (API data was fetched), OR
    // 3. Strategy is completed (has ended_at) and has no funding fee tracking
    const isSynchronized = fundingFee?.synchronized === true ||
      pnlFetched != null ||
      (strategy.ended_at != null && fundingFee == null);

    return {
      total: pnlFetched?.income_sum ?? fundingFee?.total_income ?? 0,
      eventCount: pnlFetched?.income_history?.length ?? fundingFee?.event_count ?? 0,
      synchronized: isSynchronized,
    };
  }, [ctx.funding_fee, ctx.pnl_snapshot, strategy.ended_at]);

  const localHedgePnl = ctx.pnl_snapshot?.calculated_hedge_pnl
    ?? (futuresSummary.realizedPnl + fundingSummary.total - futuresSummary.totalCommission);
  const hedgePnlSource = buildPnlSourceView(ctx.pnl_snapshot?.fetched, localHedgePnl);

  // Net P&L calculation
  const netPnL = useMemo(() => {
    // If we have a pnl_snapshot, use that (source of truth)
    if (ctx.pnl_snapshot) {
      return {
        value: ctx.pnl_snapshot.net_pnl_usdt,
        percent:
          ctx.pnl_snapshot.spot_spent_usdt > 0
            ? (ctx.pnl_snapshot.net_pnl_usdt / ctx.pnl_snapshot.spot_spent_usdt) * 100
            : 0,
        hasSnapshot: true,
      };
    }

    // Otherwise calculate from orders
    // Note: For Bithumb, order.total already includes fees (buy: +fee, sell: -fee)
    // So we do NOT subtract spotSummary.totalFees again
    const spotPnl = spotSummary.totalRevenue - spotSummary.totalCost;
    const hedgePnl = futuresSummary.realizedPnl + fundingSummary.total - futuresSummary.totalCommission;
    const netValue = spotPnl + hedgePnl;
    const percent = spotSummary.totalCost > 0 ? (netValue / spotSummary.totalCost) * 100 : 0;

    return { value: netValue, percent, hasSnapshot: false };
  }, [ctx.pnl_snapshot, spotSummary, futuresSummary, fundingSummary]);

  const pnlColor = netPnL.value >= 0 ? theme.profit : theme.loss;

  return (
    <Box>
      {/* P&L Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title={t('traderDashboard.strategyDetail.overview.invested')}
            value={formatCurrency(ctx.pnl_snapshot?.spot_spent_usdt ?? spotSummary.totalCost, 2).replace('+', '')}
            subtitle={t('traderDashboard.strategyDetail.overview.purchaseCost')}
            icon={<AccountBalanceWalletIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {(() => {
            const spotPnl = (ctx.pnl_snapshot?.spot_earned_usdt ?? spotSummary.totalRevenue) -
                           (ctx.pnl_snapshot?.spot_spent_usdt ?? spotSummary.totalCost);
            return (
              <SummaryCard
                title={t('traderDashboard.strategyDetail.overview.spotPnl')}
                value={formatCurrency(spotPnl, 2)}
                subtitle={t('traderDashboard.strategyDetail.overview.soldMinusPurchased')}
                color={spotPnl >= 0 ? theme.profit : theme.loss}
                icon={<ShowChartIcon />}
              />
            );
          })()}
        </Grid>
        <Grid item xs={12} sm={6} md={3} data-testid="overview-hedge-pnl">
          <SummaryCard
            title={t('traderDashboard.strategyDetail.overview.hedgePnl')}
            value={formatCurrency(hedgePnlSource.hedgePnl, 2)}
            subtitle={t('traderDashboard.strategyDetail.overview.includingFunding')}
            color={
              hedgePnlSource.hedgePnl >= 0
                ? theme.profit
                : theme.loss
            }
            icon={<ShowChartIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title={t('traderDashboard.strategyDetail.overview.netPnl')}
            value={formatCurrency(netPnL.value, 2)}
            subtitle={formatPercent(netPnL.percent)}
            color={pnlColor}
            icon={netPnL.value >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
          />
        </Grid>
      </Grid>

      {/* Volume Breakdown */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: theme.text.primary, fontWeight: 600, mb: 1.5 }}
        >
          {t('traderDashboard.strategyDetail.overview.volumeBreakdown')}
        </Typography>
        <Paper
          sx={{
            p: 2,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.overview.purchasedVolume')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 500 }}
              >
                {formatVolume(volumeMatching.spot.purchased)} {ctx.opportunity?.base || ''}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.overview.soldVolume')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 500 }}
              >
                {formatVolume(volumeMatching.spot.sold)} {ctx.opportunity?.base || ''}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('traderDashboard.strategyDetail.overview.remainingVolume')}
                <Tooltip title={t('traderDashboard.strategyDetail.overview.remainingVolumeTooltip')} arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 12, cursor: 'help', opacity: 0.7 }} />
                </Tooltip>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: volumeMatching.spot.matched
                    ? theme.profit  // Matched = green
                    : volumeMatching.spot.diff > 0.0001
                    ? theme.warning  // Still need to sell = yellow
                    : theme.text.primary,  // Oversold = default
                  fontFamily: 'monospace',
                  fontWeight: 500,
                }}
              >
                {formatVolume(volumeMatching.spot.diff)} {ctx.opportunity?.base || ''}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Volume Matching Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: theme.text.primary, fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {t('traderDashboard.strategyDetail.overview.volumeMatching')}
          {volumeMatching.allMatched ? (
            <CheckCircleIcon sx={{ color: theme.profit, fontSize: 18 }} />
          ) : (
            <WarningIcon sx={{ color: theme.warning, fontSize: 18 }} />
          )}
        </Typography>
        <Paper
          sx={{
            p: 2,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${volumeMatching.allMatched ? theme.profit : theme.warning}40`,
          }}
        >
          <Grid container spacing={3}>
            {/* Spot Volume Matching */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: volumeMatching.spot.matched ? 'rgba(22, 199, 132, 0.1)' : 'rgba(210, 153, 34, 0.1)',
                border: `1px solid ${volumeMatching.spot.matched ? theme.profit : theme.warning}30`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.text.primary, fontWeight: 600 }}>
                    {t('traderDashboard.strategyDetail.overview.spotMatching')}
                  </Typography>
                  {volumeMatching.spot.matched ? (
                    <CheckCircleIcon sx={{ color: theme.profit, fontSize: 14 }} />
                  ) : (
                    <WarningIcon sx={{ color: theme.warning, fontSize: 14 }} />
                  )}
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.65rem', display: 'block' }}>
                      {t('traderDashboard.strategyDetail.overview.purchased')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 500 }}>
                      {formatVolume(volumeMatching.spot.purchased, 8)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.65rem', display: 'block' }}>
                      {t('traderDashboard.strategyDetail.overview.sold')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 500 }}>
                      {formatVolume(volumeMatching.spot.sold, 8)}
                    </Typography>
                  </Grid>
                </Grid>
                {!volumeMatching.spot.matched && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.border}` }}>
                    <Typography variant="caption" sx={{ color: theme.warning, fontSize: '0.65rem' }}>
                      {t('traderDashboard.strategyDetail.overview.difference')}: {volumeMatching.spot.diff > 0 ? '+' : ''}{formatVolume(volumeMatching.spot.diff, 8)} {ctx.opportunity?.base || ''}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Futures Volume Matching */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: volumeMatching.futures.matched ? 'rgba(22, 199, 132, 0.1)' : 'rgba(210, 153, 34, 0.1)',
                border: `1px solid ${volumeMatching.futures.matched ? theme.profit : theme.warning}30`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.text.primary, fontWeight: 600 }}>
                    {t('traderDashboard.strategyDetail.overview.futuresMatching')}
                  </Typography>
                  {volumeMatching.futures.matched ? (
                    <CheckCircleIcon sx={{ color: theme.profit, fontSize: 14 }} />
                  ) : (
                    <WarningIcon sx={{ color: theme.warning, fontSize: 14 }} />
                  )}
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.65rem', display: 'block' }}>
                      {t('traderDashboard.strategyDetail.overview.openedShort')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 500 }}>
                      {formatVolume(volumeMatching.futures.opened, 8)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.65rem', display: 'block' }}>
                      {t('traderDashboard.strategyDetail.overview.closedShort')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 500 }}>
                      {formatVolume(volumeMatching.futures.closed, 8)}
                    </Typography>
                  </Grid>
                </Grid>
                {!volumeMatching.futures.matched && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.border}` }}>
                    <Typography variant="caption" sx={{ color: theme.warning, fontSize: '0.65rem' }}>
                      {t('traderDashboard.strategyDetail.overview.difference')}: {volumeMatching.futures.diff > 0 ? '+' : ''}{formatVolume(volumeMatching.futures.diff, 8)} {ctx.opportunity?.base || ''}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Funding Fee Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: theme.text.primary, fontWeight: 600, mb: 1.5 }}
        >
          {t('traderDashboard.strategyDetail.overview.fundingFees')}
        </Typography>
        <Paper
          sx={{
            p: 2,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.overview.totalFunding')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: fundingSummary.total >= 0 ? theme.profit : theme.loss,
                  fontFamily: 'monospace',
                  fontWeight: 500,
                }}
              >
                {formatCurrency(fundingSummary.total, 4)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.overview.eventCount')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 500 }}
              >
                {fundingSummary.eventCount} {t('traderDashboard.strategyDetail.overview.events')}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {fundingSummary.synchronized ? (
                  <>
                    <CheckCircleIcon sx={{ color: theme.profit, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: theme.profit }}>
                      {t('traderDashboard.strategyDetail.overview.synchronized')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <SyncIcon sx={{ color: theme.warning, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: theme.warning }}>
                      {t('traderDashboard.strategyDetail.overview.syncing')}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Profit Tiers Section */}
      {ctx.profit_tiers && ctx.profit_tiers.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: theme.text.primary, fontWeight: 600, mb: 1.5 }}
          >
            {t('traderDashboard.strategyDetail.overview.profitTiers')}
          </Typography>
          <Paper
            sx={{
              p: 2,
              backgroundColor: theme.bg.secondary,
              border: `1px solid ${theme.border}`,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {ctx.profit_tiers.map((tier, idx) => (
                <Box
                  key={idx}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      color: theme.text.primary,
                      fontWeight: 500,
                      fontSize: '0.85rem',
                    }}
                  >
                    {(tier.profit_percentage * 100).toFixed(1)}% → {(tier.volume_percentage_to_sell * 100).toFixed(0)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Warning if no P&L snapshot */}
      {!netPnL.hasSnapshot && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'rgba(210, 153, 34, 0.1)',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningIcon sx={{ color: theme.warning, fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: theme.warning }}>
            {t('traderDashboard.strategyDetail.overview.noPnlSnapshot')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OverviewTab;
